import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Menu, X, Coffee, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

function Navbar({ onToggleSidebar, sidebarOpen }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count || 0);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    window.addEventListener('notificationsRead', fetchUnread);
    return () => window.removeEventListener('notificationsRead', fetchUnread);
  }, [fetchUnread]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleBadgeClass = {
    superadmin: 'badge-superadmin',
    admin: 'badge-admin',
    user: 'badge-user',
  }[user?.role] || 'badge-user';

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 'var(--navbar-height)',
        background: 'var(--primary)',
        borderBottom: '1px solid var(--primary-dark)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(91, 164, 200, 0.3)',
      }}
    >
      {/* Hamburger */}
      <button
        onClick={onToggleSidebar}
        className="btn-icon"
        aria-label="Toggle sidebar"
        style={{ marginRight: '12px', color: 'var(--text)' }}
      >
        {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {/* Logo */}
      <Link
        to="/roster"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          textDecoration: 'none',
          color: 'var(--text)',
        }}
      >
        <Coffee size={24} strokeWidth={2} />
        <div>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.1 }}>
            Leuchtstoff
          </div>
          <div style={{ fontSize: '0.68rem', color: 'var(--primary-dark)', fontWeight: 500, lineHeight: 1 }}>
            Roster
          </div>
        </div>
      </Link>

      <div style={{ flex: 1 }} />

      {/* Bell icon */}
      <Link
        to="/notifications"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '38px',
          height: '38px',
          borderRadius: '50%',
          color: 'var(--text)',
          marginRight: '8px',
          transition: 'var(--transition)',
        }}
        className="btn-icon"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              background: 'var(--danger)',
              color: 'white',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              fontSize: '0.65rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid var(--primary)',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Link>

      {/* User dropdown */}
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setDropdownOpen((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255,255,255,0.35)',
            border: 'none',
            borderRadius: '20px',
            padding: '5px 12px 5px 5px',
            cursor: 'pointer',
            color: 'var(--text)',
            transition: 'var(--transition)',
          }}
        >
          <div
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'var(--primary-dark)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.8rem',
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <span className="navbar-username-text" style={{ fontWeight: 500, fontSize: '0.9rem', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.name || 'User'}
          </span>
          <span className={`badge ${roleBadgeClass} navbar-role-badge`} style={{ fontSize: '0.65rem' }}>
            {user?.role || 'user'}
          </span>
        </button>

        {/* Dropdown */}
        {dropdownOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              background: 'var(--white)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              border: '1px solid var(--border)',
              minWidth: '180px',
              zIndex: 200,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{user?.name}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{user?.email}</div>
            </div>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '12px 16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.9rem',
                color: 'var(--danger)',
                transition: 'var(--transition)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--danger-light)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
